/**
 * CasesFlowaiReport — FlowAI-Report (#1284, P1e)
 *
 * Klartext-Aktivitätslog: zeigt für Nicht-ITler, was FlowAI pro Fall getan
 * hat (gelesen → Vorschlag → Freigabe → MA-Antwort → Folgevorschlag) sowie
 * die Sicherheits-Schalter-Wechsel. Reine Anzeige — löst/ändert NICHTS aus.
 *
 * @page /cases/flowai-report
 * @api  GET /api/flowai/report/cases · /report/case/:id · /report/safety-log
 * @since v2.616.0 — #1284
 */
import { useState, useEffect, useCallback } from 'react';
import { Bot, Shield, Inbox, RefreshCw, MessageSquare, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

function api(path) {
  return fetch(`${API}${path}`, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
}

// Status-Pill-Farben (Border-basiert, Design-System-konform).
const STATUS_STYLE = {
  vorgeschlagen:       { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
  freigegeben:         { bg: '#ECFDF5', border: '#A7F3D0', text: '#047857' },
  gesendet:            { bg: '#ECFDF5', border: '#A7F3D0', text: '#047857' },
  ausgefuehrt:         { bg: '#ECFDF5', border: '#A7F3D0', text: '#047857' },
  antwort:             { bg: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9' },
  abgelehnt:           { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C' },
  testmodus_blockiert: { bg: '#FFFBEB', border: '#FDE68A', text: '#B45309' },
  fehlgeschlagen:      { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C' },
  abgelaufen:          { bg: '#F9FAFB', border: '#E5E7EB', text: '#6B7280' },
  gelesen:             { bg: '#F9FAFB', border: '#E5E7EB', text: '#6B7280' },
  schalter:            { bg: '#F5F3FF', border: '#DDD6FE', text: '#6D28D9' },
  kein_handlungsbedarf:{ bg: '#ECFDF5', border: '#A7F3D0', text: '#047857' },
  fehler_lauf:         { bg: '#FEF2F2', border: '#FECACA', text: '#B91C1C' },
};

function StatusPill({ statusKey, label }) {
  const s = STATUS_STYLE[statusKey] || STATUS_STYLE.gelesen;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}
    >
      {label}
    </span>
  );
}

function fmtDateTime(ts) {
  if (!ts) return '–';
  const d = new Date(ts);
  if (isNaN(d)) return '–';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function actorBadge(actor) {
  const isAi = actor === 'FlowAI';
  return (
    <span
      className="inline-flex items-center gap-1 text-[12px] font-medium"
      style={{ color: isAi ? '#338edc' : 'var(--text-secondary, #4B5563)' }}
    >
      {isAi && <Bot className="w-3.5 h-3.5" />}
      {actor}
    </span>
  );
}

export default function CasesFlowaiReport() {
  const { t } = useLanguage();
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [selected, setSelected] = useState(null); // case_id | 'safety'
  const [timeline, setTimeline] = useState(null);
  const [safetyLog, setSafetyLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  const loadCases = useCallback(() => {
    setLoadingCases(true);
    api('/api/flowai/report/cases')
      .then((d) => {
        setCases(d.cases || []);
        setSelected((prev) => prev ?? (d.cases?.[0]?.case_id ?? 'safety'));
      })
      .catch(() => setError(t('flowaiReport.loadError', 'Daten konnten nicht geladen werden.')))
      .finally(() => setLoadingCases(false));
  }, [t]);

  useEffect(() => { loadCases(); }, [loadCases]);

  useEffect(() => {
    if (selected == null) return;
    setLoadingDetail(true);
    setError(null);
    if (selected === 'safety') {
      api('/api/flowai/report/safety-log')
        .then((d) => setSafetyLog(d.events || []))
        .catch(() => setError(t('flowaiReport.loadError', 'Daten konnten nicht geladen werden.')))
        .finally(() => setLoadingDetail(false));
    } else {
      api(`/api/flowai/report/case/${selected}`)
        .then((d) => setTimeline(d))
        .catch(() => setError(t('flowaiReport.loadError', 'Daten konnten nicht geladen werden.')))
        .finally(() => setLoadingDetail(false));
    }
  }, [selected, t]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            <Bot className="w-6 h-6" style={{ color: '#338edc' }} />
            {t('flowaiReport.title', 'FlowAI-Report')}
          </h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {t('flowaiReport.subtitle', 'Was FlowAI in den Fällen getan hat — in Klartext. Reine Anzeige, es wird nichts ausgelöst.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-medium"
            style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', color: '#6B7280' }}
          >
            {t('flowaiReport.readOnly', 'Nur Anzeige (read-only)')}
          </span>
          <button
            onClick={loadCases}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium"
            style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--text-secondary, #4B5563)' }}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t('common.refresh', 'Aktualisieren')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5">
        {/* Linke Spalte: Fall-Liste + Schalter-Log */}
        <div className="space-y-3">
          <div className="rounded-[12px] border" style={{ borderColor: '#E5E7EB', background: '#fff' }}>
            <div className="px-4 py-3 border-b text-[13px] font-semibold" style={{ borderColor: '#E5E7EB', color: 'var(--text-secondary, #4B5563)' }}>
              {t('flowaiReport.casesHeader', 'Fälle mit FlowAI-Aktivität')}
            </div>
            {loadingCases ? (
              <div className="px-4 py-6 text-[13px]" style={{ color: '#9CA3AF' }}>{t('common.loading', 'Lädt…')}</div>
            ) : cases.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px]" style={{ color: '#9CA3AF' }}>
                <Inbox className="w-6 h-6 mx-auto mb-2" />
                {t('flowaiReport.noCases', 'Noch keine FlowAI-Aktivität in Fällen.')}
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                {cases.map((c) => {
                  const active = selected === c.case_id;
                  return (
                    <button
                      key={c.case_id}
                      onClick={() => setSelected(c.case_id)}
                      className="w-full text-left px-4 py-3 border-b flex items-center gap-2 transition-colors"
                      style={{
                        borderColor: '#F3F4F6',
                        background: active ? '#F0F7FF' : 'transparent',
                        borderLeft: active ? '3px solid #338edc' : '3px solid transparent',
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                          {c.case_title}
                        </div>
                        <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: '#9CA3AF' }}>
                          <span>#{c.case_id}</span>
                          <span>· {c.action_count} {t('flowaiReport.actionsShort', 'Aktionen')}</span>
                          {c.pending_count > 0 && <span style={{ color: '#1D4ED8' }}>· {c.pending_count} offen</span>}
                          {c.blocked_count > 0 && <span style={{ color: '#B45309' }}>· Testmodus</span>}
                          {c.action_count === 0 && c.no_action_count > 0 && <span style={{ color: '#047857' }}>· {t('flowaiReport.noActionShort', 'kein Handlungsbedarf')}</span>}
                          {c.error_count > 0 && <span style={{ color: '#B91C1C' }}>· {t('flowaiReport.errorShort', 'Fehler')}</span>}
                          {c.reply_count > 0 && <span style={{ color: '#6D28D9' }}>· {c.reply_count} Antwort(en)</span>}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>
                          {fmtDateTime(c.last_activity)}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: active ? '#338edc' : '#D1D5DB' }} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Schalter-Log Eintrag */}
          <button
            onClick={() => setSelected('safety')}
            className="w-full rounded-[12px] border px-4 py-3 flex items-center gap-2 text-left"
            style={{
              borderColor: '#E5E7EB',
              background: selected === 'safety' ? '#F5F3FF' : '#fff',
              borderLeft: selected === 'safety' ? '3px solid #6D28D9' : '3px solid transparent',
            }}
          >
            <Shield className="w-4 h-4" style={{ color: '#6D28D9' }} />
            <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('flowaiReport.safetyLogHeader', 'Sicherheits-Schalter (Audit)')}
            </span>
            <ChevronRight className="w-4 h-4 ml-auto" style={{ color: '#D1D5DB' }} />
          </button>
        </div>

        {/* Rechte Spalte: Zeitleiste / Schalter-Log */}
        <div className="rounded-[12px] border min-h-[300px]" style={{ borderColor: '#E5E7EB', background: '#fff' }}>
          {error && (
            <div className="px-5 py-4 text-[13px]" style={{ color: '#B91C1C' }}>{error}</div>
          )}
          {loadingDetail ? (
            <div className="px-5 py-8 text-[13px]" style={{ color: '#9CA3AF' }}>{t('common.loading', 'Lädt…')}</div>
          ) : selected === 'safety' ? (
            <SafetyLogView events={safetyLog} t={t} />
          ) : timeline ? (
            <TimelineView data={timeline} t={t} />
          ) : (
            <div className="px-5 py-8 text-[13px]" style={{ color: '#9CA3AF' }}>
              {t('flowaiReport.selectCase', 'Wähle links einen Fall aus.')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineView({ data, t }) {
  const events = data.events || [];
  return (
    <div>
      <div className="px-5 py-4 border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="text-[15px] font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
          {data.case?.title || `Fall #${data.case?.id}`}
        </div>
        <div className="text-[12px] mt-0.5" style={{ color: '#9CA3AF' }}>
          Fall #{data.case?.id} · {data.counts?.actions || 0} {t('flowaiReport.actionsShort', 'Aktionen')}
          {data.counts?.replies > 0 && ` · ${data.counts.replies} ${t('flowaiReport.repliesShort', 'Antworten')}`}
        </div>
      </div>
      {events.length === 0 ? (
        <div className="px-5 py-8 text-[13px]" style={{ color: '#9CA3AF' }}>
          {t('flowaiReport.noEvents', 'Keine Einträge für diesen Fall.')}
        </div>
      ) : (
        <ol className="px-5 py-4">
          {events.map((ev, i) => (
            <li key={`${ev.kind}-${ev.ref_id}-${i}`} className="relative pl-6 pb-5 last:pb-1">
              {/* Verbindungslinie */}
              {i < events.length - 1 && (
                <span className="absolute left-[5px] top-4 bottom-0 w-px" style={{ background: '#E5E7EB' }} />
              )}
              {/* Punkt */}
              <span
                className="absolute left-0 top-[5px] w-[11px] h-[11px] rounded-full border-2"
                style={{
                  background: '#fff',
                  borderColor: (STATUS_STYLE[ev.statusKey] || STATUS_STYLE.gelesen).text,
                }}
              />
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {ev.kind === 'reply' ? <MessageSquare className="w-3.5 h-3.5" style={{ color: '#6D28D9' }} /> : null}
                {actorBadge(ev.actor)}
                <span className="text-[11px]" style={{ color: '#9CA3AF' }}>{fmtDateTime(ev.ts)}</span>
                <StatusPill statusKey={ev.statusKey} label={ev.statusLabel} />
              </div>
              <div className="text-[13px] leading-relaxed" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                {ev.sentence}
              </div>
              {ev.beleg && (
                <div className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>{ev.beleg}</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SafetyLogView({ events, t }) {
  const list = events || [];
  return (
    <div>
      <div className="px-5 py-4 border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="text-[15px] font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
          <Shield className="w-4 h-4" style={{ color: '#6D28D9' }} />
          {t('flowaiReport.safetyLogHeader', 'Sicherheits-Schalter (Audit)')}
        </div>
        <div className="text-[12px] mt-0.5" style={{ color: '#9CA3AF' }}>
          {t('flowaiReport.safetyLogHint', 'Wer hat wann den Testmodus / die zweite Bestätigung umgestellt?')}
        </div>
      </div>
      {list.length === 0 ? (
        <div className="px-5 py-8 text-[13px]" style={{ color: '#9CA3AF' }}>
          {t('flowaiReport.noSwitches', 'Bisher keine Schalter-Wechsel protokolliert.')}
        </div>
      ) : (
        <ul className="px-5 py-4 space-y-3">
          {list.map((ev) => (
            <li key={ev.ref_id} className="flex items-start gap-3">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#6D28D9' }} />
              <div>
                <div className="text-[13px]" style={{ color: 'var(--text-secondary, #4B5563)' }}>{ev.sentence}</div>
                <div className="text-[11px] mt-0.5" style={{ color: '#9CA3AF' }}>{fmtDateTime(ev.ts)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
